import { Component, ChangeDetectionStrategy, Input } from '@angular/core';

import { AirValidationResponse, AirValidationCondition } from '@app/entities/air-validation.response';
import { SectorTripNumber } from '@user-history/entities/user-history-item';
import { AirlinePoliciesLinkPipe } from '@app/pipes/airline-policies-link.pipe';
import { AccordionSize, AccordionType } from '@design-system/components/templates/accordion/entities/accordion.types';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';

@Component({
  selector: 'app-air-terms',
  templateUrl: './air-terms.component.html',
  styleUrls: [
    '../policies-detail/policies-detail.component.sass',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AirTermsComponent {

  @Input() airValidation: AirValidationResponse;
  @Input() isMobile: boolean;
  @Input() airTermsBySector: boolean;
  @Input() showFreeCancellation: boolean = true;
  @Input() isUserHistory = false;
  @Input() isLastItem = false;

  public collapseCompleteAirTerms: boolean = false;

  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;
  public readonly fontFamily = TypographyFontFamily;

  public readonly accordionType = AccordionType.Card;
  public readonly accordionSize = AccordionSize.SM;

  constructor(
    private airlinePoliciesLinkPipe: AirlinePoliciesLinkPipe
  ) { }

  get airlinePoliciesLink(): string {
    return this.airlinePoliciesLinkPipe.transform(this.airlineCode);
  }

  get hasFareConditions(): boolean {
    return !!this.airValidation && !!this.airValidation.AirFareConditions && !!this.airValidation.AirFareConditions.length;
  }

  get airlineCode(): string {
    return this.airValidation && this.airValidation?.TripOption?.Sectors &&
      this.airValidation?.TripOption?.Sectors[0]?.Flights[0]?.OpAirlineCode ?
      this.airValidation?.TripOption?.Sectors[0]?.Flights[0]?.OpAirlineCode : null;
  }

  get airlineName(): string {
    return this.airValidation?.TripOption?.Sectors?.[0]?.Flights?.[0]?.AirlineName ?? null;
  }

  get isOneWay(): boolean {
    return !!this.airValidation?.TripOption?.Sectors && !!this.airValidation?.TripOption?.Sectors.length &&
      this.airValidation?.TripOption?.Sectors.length === 1;
  }

  get flightDeparture(): string {
    let flight = null;
    const sectors = this.airValidation?.TripOption?.Sectors;
    if (!this.airTermsBySector) {
      flight = sectors?.find(sector => sector.SectorNumber === SectorTripNumber.DEPARTURE);
    } else {
      flight = sectors?.find(sector => sector.SectorNumber === SectorTripNumber.RETURN) ||
        sectors?.find(sector => sector.SectorNumber === SectorTripNumber.DEPARTURE);
    }
    return flight ? flight.Flights?.[0]?.DepartureAirport : '';
  }

  get flightArrival(): string {
    let flight = null;
    let code = '';
    const sectors = this.airValidation?.TripOption?.Sectors;
    if (!this.airTermsBySector) {
      if (this.isOneWay) {
        flight = sectors?.find(sector => sector.SectorNumber === SectorTripNumber.DEPARTURE);
        code = flight?.Flights?.[flight?.Flights?.length - 1]?.ArrivalAirport;
      } else {
        flight = sectors?.find(sector => sector.SectorNumber === SectorTripNumber.RETURN);
        code = flight?.Flights?.[0]?.DepartureAirport;
      }
    } else {
      flight = sectors?.find(sector => sector.SectorNumber === SectorTripNumber.RETURN) ||
        sectors?.find(sector => sector.SectorNumber === SectorTripNumber.DEPARTURE);
      code = flight?.Flights?.[flight?.Flights?.length - 1]?.ArrivalAirport;
    }
    return code || '';
  }

  get showCancellationBanner(): boolean {
    return !this.isUserHistory && this.showFreeCancellation;
  }
  
  public flightKeys(flight: AirValidationCondition, index: number): string {
    const sectors = this.airValidation.TripOption.Sectors;
    return flight.Flight ? flight.Flight :
      sectors ?
        `${sectors[index].Flights[0].DepartureAirport} -
     ${sectors[index].Flights[sectors[index].Flights.length - 1].ArrivalAirport}` : '';
  }

  public roomIndexExists(roomIndex: number): boolean {
    return !isNaN(roomIndex);
  }

  public toggleAirTerms(): void {
    this.collapseCompleteAirTerms = !this.collapseCompleteAirTerms;
  }

  public isLastFareCondition(index: number,
    fareConditions: AirValidationCondition[]): boolean {
    return index === fareConditions?.length - 1;
  }

}
